import { formatISO } from "date-fns";
import { isClassBasedCycle } from "@common/helpers/reports";
import { ClassFilterType } from "@common/types/class";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { GlDrillDownOptions, resolveGlAccountCodes } from "./glDrillDown.lib";
import { AccountingReportFilters } from "./types";

export function useGLDrillDownUrl({
    startDate,
    endDate,
    accountCodes,
    entityId,
    entityIds,
    classes,
    classFilterType,
    pnlBalanceSince,
    counterpartiesIds,
    withEmptyOrUnverifiedCounterparty,
    hasCounterparty,
    accountIds,
    subaccountIds,
    ledgerId,
    journalEntrySources,
    isAllTime,
    transactionIds,
    scheduleApplicationIds,
    scheduleJeSubtypes,
}: GlDrillDownOptions) {
    const params = new URLSearchParams();

    if (startDate) {
        if (typeof startDate === "string") {
            params.append("startDate", startDate);
        } else {
            params.append(
                "startDate",
                formatISO(startDate, { representation: "date" }),
            );
        }
    }
    if (endDate) {
        if (typeof endDate === "string") {
            params.append("endDate", endDate);
        } else {
            params.append(
                "endDate",
                formatISO(endDate, { representation: "date" }),
            );
        }
    }
    if (pnlBalanceSince) {
        params.append(
            "pnlBalanceSince",
            formatISO(pnlBalanceSince, { representation: "date" }),
        );
    }
    if (entityId) {
        params.append("entityId", entityId.toString());
    }
    if (entityIds && entityIds.length > 0) {
        params.append("entityIds", entityIds.join("_"));
    }
    const resolvedAccounts = resolveGlAccountCodes(accountCodes);
    if (resolvedAccounts.classifications) {
        params.append(
            "classifications",
            resolvedAccounts.classifications.join("_"),
        );
    }
    if (resolvedAccounts.accountCodes) {
        params.append("accountCodes", resolvedAccounts.accountCodes.join("_"));
    }
    if (classes && classes.length > 0) {
        params.append("classes", classes.join("_"));
    }
    if (classFilterType) {
        params.append("classFilterType", classFilterType);
    }
    if (counterpartiesIds && counterpartiesIds.length > 0) {
        params.append("counterpartiesIds", counterpartiesIds.join("_"));
    }
    if (withEmptyOrUnverifiedCounterparty) {
        params.append("withEmptyOrUnverifiedCounterparty", "1");
    }
    if (hasCounterparty) {
        params.append("hasCounterparty", "1");
    }
    if (accountIds && accountIds.length > 0) {
        params.append("accountIds", accountIds.join("_"));
    }
    if (subaccountIds && subaccountIds.length > 0) {
        params.append("subaccountIds", subaccountIds.join("_"));
    }
    if (ledgerId) {
        params.append("ledgerId", ledgerId);
    }
    if (journalEntrySources && journalEntrySources.length > 0) {
        params.append("journalEntrySources", journalEntrySources.join(","));
    }
    if (isAllTime) {
        params.append("isAllTime", "1");
    }
    if (transactionIds && transactionIds.length > 0) {
        params.append("transactionIds", transactionIds.join("_"));
    }
    if (scheduleApplicationIds && scheduleApplicationIds.length > 0) {
        params.append(
            "scheduleApplicationIds",
            scheduleApplicationIds.join("_"),
        );
    }
    if (scheduleJeSubtypes && scheduleJeSubtypes.length > 0) {
        params.append("scheduleJeSubtypes", scheduleJeSubtypes.join(","));
    }
    return `/accounting/general-ledger?${params.toString()}`;
}

export function getDrillDownClassParams(
    period: Pick<ReportPeriodWithLabel, "isTotal" | "classIds">,
    filters: Pick<
        AccountingReportFilters,
        "classes" | "classFilterType" | "cycle"
    >,
) {
    if (period.classIds) {
        return {
            classes: period.classIds,
            classFilterType: ClassFilterType.ANY_SELECTED,
        };
    }
    if (isClassBasedCycle(filters.cycle)) {
        if (period.isTotal) {
            return { classes: undefined, classFilterType: undefined };
        }

        return {
            classes: undefined,
            classFilterType: ClassFilterType.NO_CLASSES,
        };
    }

    return {
        classes: filters.classes,
        classFilterType: filters.classFilterType,
    };
}
